"use client";

import React, { Suspense, lazy, useMemo } from "react";
import dynamic from "next/dynamic";
import { FooterVariant, FooterContent } from "./types";
import { SectionContentProps } from "../../types";

// ============================================================
// FOOTER CONTROLLER - Dynamic footer loader with lazy loading
// ============================================================
// This component handles dynamic loading of footer variants
// using Next.js dynamic imports for optimal performance.
//
// LAZY LOADING STRATEGY:
// - Edit mode: SSR disabled, show loading skeleton
// - Production: Preload critical footers, lazy load others
// ============================================================

// Lazy load all footer variants
// In production, these are code-split into separate chunks
const CorporateFooter = lazy(
  () => import("./variants/CorporateFooter")
);

const MinimalFooter = lazy(
  () => import("./variants/MinimalFooter")
);

const BigBrandFooter = lazy(
  () => import("./variants/BigBrandFooter")
);

const ThreeColumnFooter = lazy(
  () => import("./variants/ThreeColumnFooter")
);

const ModernFooter = lazy(
  () => import("./variants/ModernFooter")
);

const UniversityFooter = lazy(
  () => import("../../university/universityfooter/UniversityFooter")
);

const EducaeetFooter = lazy(
  () => import("../../educaeet/educaeetfooter/EducaeetFooter").then(m => ({ default: m.EducaeetFooter }))
);

const HokFooter = lazy(
  () => import("../../hok/hokfooter/HokFooter")
);

const OvanFooter = lazy(
  () => import("../../ovan/ovanfooter/OvanFooter")
);

const CollegeFooter = lazy(
  () => import("../../college/collegefooter/CollegeFooter")
);

const SaasFooter = lazy(
  () => import("../../saas/saasfooter/SaasFooter")
);

const CorporateFooterCustom = lazy(
  () => import("../../corporate/corporatefooter/CorporateFooter")
);

const AIFooter = lazy(
  () => import("../../aistartup/aifooter/AIFooter")
);

const PortfolioFooter = lazy(
  () => import("../../portfolio/portfoliofooter/PortfolioFooter")
);

// ============================================================
// FOOTER REGISTRY - Maps variants to their components
// ============================================================

export const FOOTER_REGISTRY: Record<FooterVariant, React.ComponentType<any>> = {
  "corporate": CorporateFooterCustom,
  "minimal": MinimalFooter,
  "big-brand": BigBrandFooter,
  "three-column": ThreeColumnFooter,
  "modern": ModernFooter,
  "university": UniversityFooter,
  "educaeet": EducaeetFooter,
  "hok": HokFooter,
  "ovan": OvanFooter,
  "college": CollegeFooter,
  "saas": SaasFooter,
  "aistartup": AIFooter,
  "portfolio": PortfolioFooter,
};


// Footer variant metadata for UI
export const FOOTER_VARIANTS = [
  {
    id: "corporate" as FooterVariant,
    label: "Corporate",
    description: "Classic 4-column layout for enterprise and business",
    preview: "🏢",
  },
  {
    id: "minimal" as FooterVariant,
    label: "Minimal",
    description: "Clean and simple for minimalist designs",
    preview: "⚡",
  },
  {
    id: "big-brand" as FooterVariant,
    label: "Big Brand",
    description: "Bold brand presence with prominent CTAs",
    preview: "🎯",
  },
  {
    id: "three-column" as FooterVariant,
    label: "Three Column",
    description: "Balanced layout with clear navigation",
    preview: "📊",
  },
  {
    id: "modern" as FooterVariant,
    label: "Modern",
    description: "Contemporary design with newsletter focus",
    preview: "🚀",
  },
  {
    id: "university" as FooterVariant,
    label: "University",
    description: "Laurel emblem logo, links columns, and action CTAs",
    preview: "🎓",
  },
  {
    id: "educaeet" as FooterVariant,
    label: "Educaeet",
    description: "Modern education platform footer with contact info and social links",
    preview: "📚",
  },
  {
    id: "hok" as FooterVariant,
    label: "Hok",
    description: "Digital marketplace footer with newsletter and social links",
    preview: "🛒",
  },
] as const;

// ============================================================
// LOADING SKELETON
// ============================================================

const FooterLoadingSkeleton: React.FC = () => (
  <footer className="bg-slate-900 animate-pulse" role="status" aria-label="Loading footer">
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="grid grid-cols-4 gap-12 mb-16">
        {[...Array(4)].map((_, i) => (
          <div key={i}>
            <div className="h-6 bg-slate-800 rounded w-24 mb-4" />
            <div className="space-y-3">
              <div className="h-4 bg-slate-800 rounded w-full" />
              <div className="h-4 bg-slate-800 rounded w-3/4" />
              <div className="h-4 bg-slate-800 rounded w-5/6" />
            </div>
          </div>
        ))}
      </div>
      <div className="h-12 bg-slate-800 rounded w-full" />
    </div>
  </footer>
);

// ============================================================
// FOOTER CONTROLLER PROPS - Extends SectionContentProps for compatibility
// ============================================================

export interface FooterControllerProps extends SectionContentProps {
  variant?: FooterVariant;
  className?: string;
}

// ============================================================
// DYNAMIC EDITABLE IMPORTS (Must be outside component to prevent remounting)
// ============================================================
const LocalCorporateFooter = dynamic(() => import("./variants/CorporateFooter"), { ssr: false, loading: () => <FooterLoadingSkeleton /> });
const LocalMinimalFooter = dynamic(() => import("./variants/MinimalFooter"), { ssr: false, loading: () => <FooterLoadingSkeleton /> });
const LocalBigBrandFooter = dynamic(() => import("./variants/BigBrandFooter"), { ssr: false, loading: () => <FooterLoadingSkeleton /> });
const LocalThreeColumnFooter = dynamic(() => import("./variants/ThreeColumnFooter"), { ssr: false, loading: () => <FooterLoadingSkeleton /> });
const LocalModernFooter = dynamic(() => import("./variants/ModernFooter"), { ssr: false, loading: () => <FooterLoadingSkeleton /> });
const LocalUniversityFooter = dynamic(() => import("../../university/universityfooter/UniversityFooter"), { ssr: false, loading: () => <FooterLoadingSkeleton /> });
const LocalEducaeetFooter = dynamic(() => import("../../educaeet/educaeetfooter/EducaeetFooter").then(m => m.EducaeetFooter), { ssr: false, loading: () => <FooterLoadingSkeleton /> });
const LocalHokFooter = dynamic(() => import("../../hok/hokfooter/HokFooter"), { ssr: false, loading: () => <FooterLoadingSkeleton /> });
const LocalOvanFooter = dynamic(() => import("../../ovan/ovanfooter/OvanFooter"), { ssr: false, loading: () => <FooterLoadingSkeleton /> });

const EDITABLE_REGISTRY: Record<string, React.ComponentType<any>> = {
  "corporate": LocalCorporateFooter,
  "minimal": LocalMinimalFooter,
  "big-brand": LocalBigBrandFooter,
  "three-column": LocalThreeColumnFooter,
  "modern": LocalModernFooter,
  "university": LocalUniversityFooter,
  "educaeet": LocalEducaeetFooter,
  "hok": LocalHokFooter,
  "ovan": LocalOvanFooter,
};


// ============================================================
// MAIN CONTROLLER COMPONENT
// ============================================================

export const FooterController: React.FC<FooterControllerProps> = ({
  variant: propVariant,
  content = {},
  isEditing = false,
  isSelected = false,
  onContentChange,
  className = "",
  defaultLogo,
}) => {
  // Merge defaultLogo into content if not already set
  const mergedContent = {
    ...content,
    logo: content.logo || defaultLogo,
  };
  // Get variant from props first, then fallback to content.variant, then default to "corporate"
  const variant = useMemo(() => {
    if (propVariant) return propVariant;
    const contentVariant = content?.variant;
    if (typeof contentVariant === "string" && contentVariant) {
      // Normalize variant names (handle both "corporate" and "footer-corporate")
      if (contentVariant.includes("corporate")) return "corporate" as FooterVariant;
      if (contentVariant.includes("minimal")) return "minimal" as FooterVariant;
      if (contentVariant.includes("big-brand") || contentVariant.includes("bigbrand")) return "big-brand" as FooterVariant;
      if (contentVariant.includes("three-column") || contentVariant.includes("threecolumn")) return "three-column" as FooterVariant;
      if (contentVariant.includes("modern")) return "modern" as FooterVariant;
      if (contentVariant.includes("university")) return "university" as FooterVariant;
      if (contentVariant.includes("educaeet")) return "educaeet" as FooterVariant;
      if (contentVariant.includes("hok")) return "hok" as FooterVariant;
      if (contentVariant.includes("ovan")) return "ovan" as FooterVariant;
      return contentVariant as FooterVariant;
    }
    return "corporate" as FooterVariant;
  }, [propVariant, content?.variant]);

  // Get the component from registry
  const FooterComponent = FOOTER_REGISTRY[variant];

  if (!FooterComponent) {
    return (
      <div className="bg-red-900 text-white p-8 text-center" role="alert">
        <p>Error: Unknown footer variant "{String(variant)}"</p>
      </div>
    );
  }

  // Edit mode: No SSR for better editing experience
  if (isEditing) {
    const EditableFooterComponent = EDITABLE_REGISTRY[variant] || LocalCorporateFooter;

    return (
      <div className={className}>
        <EditableFooterComponent
          content={mergedContent}
          isEditing={isEditing}
          isSelected={isSelected}
          onContentChange={onContentChange}
          defaultLogo={defaultLogo}
        />
      </div>
    );
  }

  // Production mode: Standard lazy loading
  return (
    <div className={className}>
      <Suspense
        fallback={
          process.env.NODE_ENV === "development" ? (
            <FooterLoadingSkeleton />
          ) : null
        }
      >
        <FooterComponent
          content={mergedContent}
          isEditing={isEditing}
          isSelected={isSelected}
          onContentChange={onContentChange}
          defaultLogo={defaultLogo}
        />
      </Suspense>
    </div>
  );
};

// ============================================================
// EXPORTS
// ============================================================

export default FooterController;
